const STORAGE_KEY = 'device_fingerprint';

/**
 * 生成 Canvas 指纹
 */
const getCanvasFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Fingerprint 🎨', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Fingerprint 🎨', 4, 17);

    return canvas.toDataURL();
  } catch {
    return '';
  }
};

/**
 * 生成 WebGL 指纹
 */
const getWebGLFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';

    const glContext = gl as WebGLRenderingContext;
    const debugInfo = glContext.getExtension('WEBGL_debug_renderer_info');
    const vendor = debugInfo ? glContext.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : '';
    const renderer = debugInfo ? glContext.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : '';

    return `${vendor}|${renderer}`;
  } catch {
    return '';
  }
};

/**
 * 获取可用字体列表指纹
 */
const getFontsFingerprint = (): string => {
  const testFonts = [
    'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New', 'Georgia',
    'Impact', 'Lucida Console', 'Lucida Sans Unicode', 'Microsoft Sans Serif',
    'Palatino Linotype', 'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana',
    'SimSun', 'SimHei', 'Microsoft YaHei', 'Microsoft JhengHei', 'PingFang SC',
    'Heiti SC', 'STHeiti', 'WenQuanYi Micro Hei', 'Noto Sans CJK SC',
  ];

  const availableFonts: string[] = [];
  const testString = 'mmmmmmmmmmlli';
  const testSize = '72px';

  const body = document.body;
  const span = document.createElement('span');
  span.style.position = 'absolute';
  span.style.left = '-9999px';
  span.style.fontSize = testSize;
  span.style.lineHeight = 'normal';
  span.textContent = testString;
  body.appendChild(span);

  const defaultWidth = span.offsetWidth;
  const defaultHeight = span.offsetHeight;

  for (const font of testFonts) {
    span.style.fontFamily = `'${font}', monospace`;
    if (span.offsetWidth !== defaultWidth || span.offsetHeight !== defaultHeight) {
      availableFonts.push(font);
    }
  }

  body.removeChild(span);
  return availableFonts.join(',');
};

/**
 * 简单的哈希函数（djb2）
 */
const hashString = (str: string): string => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * 获取设备指纹
 * 不绑定 UA，不绑定 IP
 * 使用 Canvas 指纹 + WebGL 指纹 + 屏幕分辨率 + 时区 + 语言 + 可用字体列表的组合
 */
export const getDeviceFingerprint = (): string => {
  // 先检查 localStorage 缓存
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      return cached;
    }
  } catch {
    // localStorage 不可用时继续生成
  }

  const components: string[] = [];

  // Canvas 指纹
  const canvasFP = getCanvasFingerprint();
  if (canvasFP) {
    components.push(hashString(canvasFP));
  }

  // WebGL 指纹
  const webglFP = getWebGLFingerprint();
  if (webglFP) {
    components.push(hashString(webglFP));
  }

  // 屏幕分辨率
  components.push(`${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`);

  // 时区
  components.push(String(Intl.DateTimeFormat().resolvedOptions().timeZone));

  // 语言
  components.push(navigator.language);

  // 可用字体列表
  const fontsFP = getFontsFingerprint();
  if (fontsFP) {
    components.push(hashString(fontsFP));
  }

  // 组合所有组件生成最终指纹
  const rawFingerprint = components.join('|');
  const fingerprint = hashString(rawFingerprint);

  // 缓存到 localStorage
  try {
    localStorage.setItem(STORAGE_KEY, fingerprint);
  } catch {
    // localStorage 不可用时忽略
  }

  return fingerprint;
};
